import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePostUploadConferenceAbstractApi } from '@/api/conference';
import oralPresentationFormFile from '@/assets/files/구두발표초록양식및신청서.hwp';
import posterPresentationFormFile from '@/assets/files/포스터발표초록양식및신청서.hwp';
import AbstractModal from '@/components/common/Modal/AbstractModal/AbstractModal';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';
import { OutlineLinkButton, PageActionRow } from '@/components/common/form/formPageStyles';
import DownloadIcon from '@/components/Icon/DownloadIcon';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import { CONF_TITLE } from '@/constants/menu';
import { normalizeSocietyMenu, SOCIETY_STEP_ABSTRACT_CONFIRM } from '@/constants/society';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

type AbstractGuideLine = {
	className?: string;
	text: string;
};

const AbstractGuide = () => {
	const { t } = useTranslation();
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();
	const navigate = useNavigate();
	const location = useLocation();

	const [isOpen, setIsOpen] = useState(false);
	const [presentation, setPresentation] = useState('');
	const [section, setSection] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	const params = normalizeSocietyMenu(location.state?.menu);
	const guideLines = t('society.abstract.guideLines', { returnObjects: true }) as AbstractGuideLine[];

	const { noticeLines, sampleLines } = useMemo(() => {
		const notices: AbstractGuideLine[] = [];
		const samples: AbstractGuideLine[] = [];

		guideLines.forEach((line) => {
			if (line.className === 'left') {
				notices.push(line);
			} else {
				samples.push(line);
			}
		});

		return { noticeLines: notices, sampleLines: samples };
	}, [guideLines]);

	const { mutateAsync: uploadConferenceAbstract } = usePostUploadConferenceAbstractApi();

	const handleAbstractRegister = async () => {
		if (!isLogin()) {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}

		setIsOpen(true);
	};

	const getAbstractInfo = (presentationValue: string, sectionValue: string) => {
		setPresentation(presentationValue);
		setSection(sectionValue);

		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			showModal(t('society.abstract.fileRequired'), 'confirm');
			return;
		}

		showModal(t('society.abstract.confirmUpload', { presentation: t(presentation), section: t(section) }), 'register', '', () => uploadFile(file));
	};

	const uploadFile = async (file: File) => {
		try {
			const formData = new FormData();
			formData.append('abstract_file', file);
			formData.append('conference_name', CONF_TITLE);
			formData.append('abstract_presentation', presentation);
			formData.append('abstract_division', section);
			const response = await uploadConferenceAbstract(formData);

			if (response.data?.success) {
				showModal(t('society.abstract.registerSuccess'), 'success');
			} else {
				showModal(t('society.abstract.registerFailed'), 'error');
			}
		} catch (error) {
			console.error('abstract upload error:', error);
			showModal(t('society.abstract.registerError'), 'error');
		}
	};

	const handleAbstractConfirm = async () => {
		if (!isLogin()) {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}
		navigate('/society', { state: { step: SOCIETY_STEP_ABSTRACT_CONFIRM, menu: params } });
	};

	return (
		<S.Container>
			<S.SectionCard>
				<S.CardBody>
					<S.SectionHeader>
						<S.ContentTitle>{t('society.abstract.guideTitle')}</S.ContentTitle>
						<S.LeadBox>
							<S.LeadText>{t('society.abstract.guideDescriptionLine1')}</S.LeadText>
							<S.LeadHint>{t('society.abstract.guideDescriptionLine2')}</S.LeadHint>
						</S.LeadBox>
					</S.SectionHeader>

					<input
						type="file"
						ref={fileInputRef}
						style={{ display: 'none' }}
						accept=".hwp,.docx,.doc,application/x-hwp,application/haansofthwp,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
						onClick={(e) => {
							(e.target as HTMLInputElement).value = '';
						}}
						onChange={handleFileChange}
					/>
					<PageActionRow>
						<PrimaryButton type="button" onClick={handleAbstractRegister}>
							{t('society.abstract.actions.register')}
							<SingleArrowNext iconColor="currentColor" width="20" height="20" />
						</PrimaryButton>
						<OutlineButton type="button" onClick={handleAbstractConfirm}>
							{t('society.abstract.actions.confirm')}
							<SingleArrowNext iconColor="currentColor" width="20" height="20" />
						</OutlineButton>
					</PageActionRow>

					{noticeLines.length > 0 && (
						<S.NoticeList>
							{noticeLines.map((line, index) => (
								<S.NoticeItem key={`notice-${index}`}>{line.text}</S.NoticeItem>
							))}
						</S.NoticeList>
					)}
				</S.CardBody>
			</S.SectionCard>

			{sampleLines.length > 0 && (
				<S.SectionCard>
					<S.CardBody>
						<S.SectionTitle>{t('society.abstract.sampleTitle')}</S.SectionTitle>
						<S.ManuscriptPreview>
							{sampleLines.map((line, index) => (
								<S.GuideLine key={`sample-${index}`} className={line.className}>
									{line.text}
								</S.GuideLine>
							))}
						</S.ManuscriptPreview>
					</S.CardBody>
				</S.SectionCard>
			)}

			<S.SectionCard>
				<S.CardBody>
					<S.SectionTitle>{t('society.abstract.downloadTitle')}</S.SectionTitle>
					<S.DownloadGrid>
						<OutlineLinkButton href={oralPresentationFormFile} download>
							<DownloadIcon />
							{t('society.abstract.download.ko')}
						</OutlineLinkButton>
						<OutlineLinkButton href={posterPresentationFormFile} download>
							<DownloadIcon />
							{t('society.abstract.download.en')}
						</OutlineLinkButton>
					</S.DownloadGrid>
				</S.CardBody>
			</S.SectionCard>

			{isOpen && (
				<ModalPortal>
					<ModalBox onClick={() => setIsOpen(false)}>
						<AbstractModal
							closeModal={setIsOpen}
							onComplete={({ presentation, section }) => {
								getAbstractInfo(presentation, section);
							}}
						/>
					</ModalBox>
				</ModalPortal>
			)}
		</S.Container>
	);
};

export default AbstractGuide;
